import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { add, deleteRecord, fetch, update } from '../services/MetersService';
import { Meter } from '../types';
import { notifySuccess, notifyError } from '../../../components/ServiceNotifications';
import { buildCreateMeterInput, buildUpdateMeterInput } from '../services/buildMeterInput';

export const useMeter = (meterId?: string) => {
    const queryClient = useQueryClient();

    // FETCH
    const meterQuery = useQuery({
        queryKey: ['meter', meterId],
        queryFn: () => fetch(meterId!),
        enabled: !!meterId
    });

    // CREATE MUTATION
    const createMutation = useMutation({
        mutationFn: (values: Meter) => add(buildCreateMeterInput(values)),
        onSuccess: () => {
            notifySuccess('Meter created');
            queryClient.invalidateQueries({ queryKey: ['meters'] });
        },
        onError: notifyError
    });

    // UPDATE MUTATION
    // NB (FNP-768): the meters edit flow does NOT go through this mutation — RecordsTable's
    // handleDrawerSubmit owns the edit path (field update + the setMeterStatus transition
    // routing + the RETIRE confirm). This mutation is retained for the standard hook surface;
    // if a caller ever drives edits through it, route status transitions the same way.
    const updateMutation = useMutation({
        mutationFn: (values: Meter) => {
            if (!meterId) throw new Error('meterId is required parameter');
            return update(meterId, buildUpdateMeterInput(values) as Meter);
        },
        onSuccess: () => {
            notifySuccess('Meter updated');
            queryClient.invalidateQueries({ queryKey: ['meters'] });
            queryClient.invalidateQueries({ queryKey: ['meter', meterId] });
        },
        onError: notifyError
    });

    // DELETE MUTATION
    const deleteMutation = useMutation({
        mutationFn: () => {
            if (!meterId) throw new Error('meterId is required parameter');
            return deleteRecord(meterId);
        },
        onSuccess: () => {
            notifySuccess('Meter deleted');
            queryClient.invalidateQueries({ queryKey: ['meters'] });
        },
        onError: notifyError
    });

    return {
        record: meterQuery.data,
        loading: meterQuery.isLoading,
        fetchRecord: meterQuery.refetch,
        createRecord: createMutation.mutate,
        updateRecord: updateMutation.mutate,
        deleteRecord: deleteMutation.mutate,
        createMutation,
        updateMutation,
        deleteMutation
    };
};